/**
 * EntityListCompact - Compact entity list for document panel
 *
 * Displays entities grouped by domain (Market, Business, Solution, Insights)
 * with collapsible sections. Designed for use in the UnifiedDocumentPanel.
 */

import { useState, useMemo } from 'react'
import { ChevronRight, ChevronDown, CheckCircle2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { type Document2Entity } from '@/lib/api/client'
import { DOMAIN_CONFIG, getEntityDomain, type DomainKey } from '@/lib/constants/entityDomainConfig'

interface EntityListCompactProps {
  entities: Document2Entity[]
  selectedEntityId: string | null
  onEntitySelect: (entityId: string) => void
}

export function EntityListCompact({
  entities,
  selectedEntityId,
  onEntitySelect,
}: EntityListCompactProps) {
  const [expandedDomains, setExpandedDomains] = useState<Set<DomainKey>>(
    new Set(['Market', 'Business', 'Solution', 'Insights'])
  )

  // Group entities by domain
  const entitiesByDomain = useMemo(() => {
    const grouped: Record<DomainKey, Document2Entity[]> = {
      Market: [],
      Business: [],
      Solution: [],
      Insights: [],
    }

    for (const entity of entities) {
      const domain = getEntityDomain(entity.entityType)
      grouped[domain].push(entity)
    }

    return grouped
  }, [entities])

  const toggleDomain = (domain: DomainKey) => {
    const next = new Set(expandedDomains)
    if (next.has(domain)) {
      next.delete(domain)
    } else {
      next.add(domain)
    }
    setExpandedDomains(next)
  }

  return (
    <div className="space-y-1">
      {(Object.entries(DOMAIN_CONFIG) as [DomainKey, typeof DOMAIN_CONFIG.Market][]).map(
        ([domain, config]) => {
          const domainEntities = entitiesByDomain[domain]
          if (domainEntities.length === 0) return null

          const Icon = config.icon
          const isExpanded = expandedDomains.has(domain)

          return (
            <div key={domain}>
              {/* Domain Header */}
              <button
                onClick={() => toggleDomain(domain)}
                className={`w-full flex items-center gap-2 p-2 rounded-lg transition-colors hover:bg-warm-100 ${config.bgColor}`}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3 w-3 text-text-muted" />
                ) : (
                  <ChevronRight className="h-3 w-3 text-text-muted" />
                )}
                <Icon className={`h-3.5 w-3.5 ${config.textColor}`} />
                <span className={`text-sm font-medium ${config.textColor}`}>{domain}</span>
                <Badge variant="secondary" className="ml-auto text-xs bg-white/50 py-0 px-1.5">
                  {domainEntities.length}
                </Badge>
              </button>

              {/* Entity List */}
              {isExpanded && (
                <div className="ml-4 mt-1 space-y-0.5">
                  {domainEntities.map((entity) => (
                    <button
                      key={entity.id}
                      onClick={() => onEntitySelect(entity.id)}
                      className={`w-full text-left p-2 rounded-lg transition-all text-sm ${
                        selectedEntityId === entity.id
                          ? `${config.bgColor} ring-1 ring-offset-1`
                          : 'hover:bg-warm-50'
                      }`}
                      style={{
                        ringColor: selectedEntityId === entity.id ? config.color : undefined,
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate text-text-primary text-xs">
                          {entity.name}
                        </span>
                        {entity.status === 'created' && (
                          <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5">
                        {entity.evidence.length} evidence
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        }
      )}

      {entities.length === 0 && (
        <div className="text-center py-4 text-text-muted text-sm">
          No entities extracted
        </div>
      )}
    </div>
  )
}
